/**
 * Semantic NULL Configuration
 *
 * Defines what NULL means in different business contexts.
 */

import type { DateSemanticType, SemanticConfig, SemanticConfigMap } from '../types'

/**
 * Complete semantic configuration for all supported types.
 */
export const SEMANTIC_CONFIG: SemanticConfigMap = {
  // NULL = "Effective immediately upon activation"
  on_activation: {
    displayLabel: 'Effective Immediately',
    buttonLabel: 'Immediately',
    description: 'Will become effective the moment it is activated',
  },

  // NULL = "No end date / runs indefinitely"
  end_of_time: {
    displayLabel: 'No End Date',
    buttonLabel: 'No End',
    description: 'Will remain effective indefinitely until superseded',
  },

  // NULL = "Beginning of time / always valid"
  beginning_of_time: {
    displayLabel: 'Beginning of Time',
    buttonLabel: 'Always',
    description: 'Has always been valid since the beginning',
  },

  // Default when no semantics defined - just "Clear"
  default: {
    displayLabel: 'Not Set',
    buttonLabel: 'Clear',
    description: 'Clear the date value',
  },
}

/**
 * Get the semantic configuration for a given type.
 *
 * @param semanticType - The semantic type from schema, or null/undefined
 * @returns SemanticConfig for display
 */
export function getSemanticConfig(
  semanticType: DateSemanticType | null | undefined
): SemanticConfig {
  if (!semanticType) {
    return SEMANTIC_CONFIG.default
  }

  return SEMANTIC_CONFIG[semanticType] ?? SEMANTIC_CONFIG.default
}

/**
 * Check if a semantic type is valid/known.
 */
export function isValidSemanticType(type: string | null | undefined): type is DateSemanticType {
  if (!type) return false
  return type in SEMANTIC_CONFIG && type !== 'default'
}
